/**
 * The Q2.4 "Other names" rule the vetting form checks before it posts
 * (cca-49qxu): every row needs a family name, a row that only repeats the
 * current name is refused, and rows and lengths are bounded.
 *
 * The TWIN of cca-parishes-worker's src/vetting/intake-fields.ts
 * (`otherNamesProblem`), which the consent and paper routes run on the same
 * body. The page cannot import a worker, so the rule and its sentences live in
 * both places, word for word. Change one, change the other.
 */

/** Most other-names rows one application may carry. */
export const OTHER_NAMES_MAX_ROWS = 20;

/** Longest family, first or middle name in one row. */
export const OTHER_NAME_MAX_LENGTH = 100;

/** The two sentences about ONE row name it, counted from 1 as the table shows
 *  its rows (Miranda r1 M-3/M-4). */
export const OTHER_NAMES_MESSAGES = {
  tooMany: `Up to ${OTHER_NAMES_MAX_ROWS} other names can be given in 2.4. If there are more, tell your parish so they can be added by hand.`,
  tooLong: `Each name in 2.4 can be up to ${OTHER_NAME_MAX_LENGTH} characters.`,
  needsFamilyName: (row: number): string =>
    `Row ${row} in 2.4 needs a surname. Add it, or clear that row.`,
  repeatsCurrent: (row: number): string =>
    `Row ${row} in 2.4 is the same as the name in 2.3. 2.4 is only for other names — change that row or clear it.`,
} as const;

/** A 2.4 refusal: its sentence, and the 1-based row it is about — null when
 *  it is about the list as a whole. The page marks that row's three boxes. */
export interface OtherNamesProblem {
  message: string;
  row: number | null;
}

export interface OtherNameRow {
  familyName: string;
  firstName: string;
  middleNames: string;
}

/** The applicant's current name, as section 2.3 holds it. */
export interface CurrentName {
  familyName: string;
  firstName: string;
  middleNames: string;
}

/** Trimmed, inner whitespace collapsed, lower-cased. */
function norm(v: string): string {
  return v.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** A row with nothing typed in any cell is not a row: it is neither sent nor checked. */
export function isBlankOtherName(row: OtherNameRow): boolean {
  return !norm(row.familyName) && !norm(row.firstName) && !norm(row.middleNames);
}

/**
 * True when the row only repeats the current name: the same family name, and
 * either the same first and middle names or the same first name(s) with no
 * middle names given. Case and spacing aside; any other difference is a
 * different name.
 */
export function repeatsCurrentName(row: OtherNameRow, current: CurrentName): boolean {
  const family = norm(current.familyName);
  if (!family || norm(row.familyName) !== family) return false;
  const first = norm(current.firstName);
  const given = norm(`${first} ${norm(current.middleNames)}`);
  if (norm(`${norm(row.firstName)} ${norm(row.middleNames)}`) === given) return true;
  return !norm(row.middleNames) && norm(row.firstName) === first;
}

/** null when the rows are acceptable, else the sentence 2.4 shows and the row it is about. */
export function otherNamesProblem(
  rows: readonly OtherNameRow[],
  current: CurrentName,
): OtherNamesProblem | null {
  if (rows.length > OTHER_NAMES_MAX_ROWS) {
    return { message: OTHER_NAMES_MESSAGES.tooMany, row: null };
  }
  for (const [i, row] of rows.entries()) {
    const cells = [row.familyName, row.firstName, row.middleNames];
    if (cells.some((v) => v.length > OTHER_NAME_MAX_LENGTH)) {
      return { message: OTHER_NAMES_MESSAGES.tooLong, row: i + 1 };
    }
  }
  for (const [i, row] of rows.entries()) {
    if (isBlankOtherName(row)) continue;
    if (!norm(row.familyName)) {
      return { message: OTHER_NAMES_MESSAGES.needsFamilyName(i + 1), row: i + 1 };
    }
    if (repeatsCurrentName(row, current)) {
      return { message: OTHER_NAMES_MESSAGES.repeatsCurrent(i + 1), row: i + 1 };
    }
  }
  return null;
}
